<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import {
  createNewContact,
  fetchContactableInboxes,
} from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();
const router = useRouter();

const currentUser = useMapGetter('getCurrentUser');
const accountId = useMapGetter('getCurrentAccountId');
const inboxes = useMapGetter('inboxes/getInboxes');

// Phone-capable channels. Covers uazapi/Evolution regardless of whether the
// inbox is registered as a WhatsApp channel or a generic API channel.
const PHONE_CHANNELS = [
  INBOX_TYPES.WHATSAPP,
  INBOX_TYPES.API,
  INBOX_TYPES.TWILIO,
  INBOX_TYPES.SMS,
];

const getChannelType = inbox => inbox.channelType || inbox.channel_type;

const whatsappInboxes = computed(() =>
  (inboxes.value || []).filter(inbox =>
    PHONE_CHANNELS.includes(getChannelType(inbox))
  )
);

const isOpen = ref(false);
const isSending = ref(false);
const selectedInboxId = ref(null);
const phone = ref('');
const message = ref('');

// Keep only digits and re-add the leading '+' so createNewContact treats the
// value as a phone number (it keys off the '+' prefix).
const normalizedPhone = computed(() => {
  const digits = (phone.value || '').replace(/\D/g, '');
  return digits ? `+${digits}` : '';
});

const canSend = computed(
  () =>
    !!selectedInboxId.value &&
    normalizedPhone.value.length >= 9 &&
    message.value.trim().length > 0 &&
    !isSending.value
);

const resetForm = () => {
  phone.value = '';
  message.value = '';
};

const open = () => {
  isOpen.value = true;
  store.dispatch('inboxes/get');
  if (whatsappInboxes.value.length === 1) {
    selectedInboxId.value = whatsappInboxes.value[0].id;
  }
};

const close = () => {
  isOpen.value = false;
  resetForm();
  emit('close');
};

const toggle = () => {
  if (isOpen.value) {
    close();
  } else {
    open();
  }
};

const send = async () => {
  if (!canSend.value) {
    useAlert(t('CONVERSATION.QUICK_SEND.VALIDATION'));
    return;
  }
  isSending.value = true;
  try {
    // 1. Create the contact straight from the number (name comes from WhatsApp later)
    const contact = await createNewContact(normalizedPhone.value);
    // 2. Resolve the source_id for the chosen inbox
    const contactableInboxes = await fetchContactableInboxes(contact.id);
    const target = contactableInboxes.find(
      inbox => inbox.id === selectedInboxId.value
    );
    if (!target || !target.sourceId) {
      useAlert(t('CONVERSATION.QUICK_SEND.ERROR'));
      return;
    }
    // 3. Create the conversation with the outgoing message
    const data = await store.dispatch('contactConversations/create', {
      params: {
        inboxId: selectedInboxId.value,
        sourceId: target.sourceId,
        contactId: Number(contact.id),
        message: { content: message.value.trim() },
        assigneeId: currentUser.value.id,
        mailSubject: '',
      },
      isFromWhatsApp: false,
    });
    useAlert(t('CONVERSATION.QUICK_SEND.SUCCESS'));
    close();
    router.push(`/app/accounts/${accountId.value}/conversations/${data.id}`);
  } catch (error) {
    useAlert(t('CONVERSATION.QUICK_SEND.ERROR'));
  } finally {
    isSending.value = false;
  }
};

useKeyboardEvents({
  Escape: {
    action: () => {
      if (isOpen.value) close();
    },
  },
});
</script>

<template>
  <div class="relative">
    <slot name="trigger" :toggle="toggle" :is-open="isOpen" />
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-start justify-center pt-[clamp(3rem,15vh,12rem)] bg-n-alpha-black1 backdrop-blur-[4px]"
      @click.self="close"
    >
      <div
        class="w-[26rem] max-w-[92vw] bg-n-solid-2 rounded-xl shadow-lg outline outline-1 outline-n-container p-5 flex flex-col gap-4"
      >
        <div class="flex items-center justify-between">
          <h3 class="m-0 text-base font-medium text-n-slate-12">
            {{ t('CONVERSATION.QUICK_SEND.TITLE') }}
          </h3>
          <Button icon="i-lucide-x" slate faded xs @click="close" />
        </div>

        <p v-if="!whatsappInboxes.length" class="m-0 text-sm text-n-slate-11">
          {{ t('CONVERSATION.QUICK_SEND.NO_INBOX') }}
        </p>

        <template v-else>
          <label class="flex flex-col gap-1">
            <span class="text-sm text-n-slate-12">
              {{ t('CONVERSATION.QUICK_SEND.INBOX_LABEL') }}
            </span>
            <select
              v-model="selectedInboxId"
              class="reset-base h-9 px-2 text-sm border rounded-lg border-n-weak bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12 focus:outline-none"
            >
              <option :value="null" disabled>
                {{ t('CONVERSATION.QUICK_SEND.INBOX_PLACEHOLDER') }}
              </option>
              <option
                v-for="inbox in whatsappInboxes"
                :key="inbox.id"
                :value="inbox.id"
              >
                {{ inbox.name }}
              </option>
            </select>
          </label>

          <label class="flex flex-col gap-1">
            <span class="text-sm text-n-slate-12">
              {{ t('CONVERSATION.QUICK_SEND.NUMBER_LABEL') }}
            </span>
            <input
              v-model="phone"
              type="tel"
              :placeholder="t('CONVERSATION.QUICK_SEND.NUMBER_PLACEHOLDER')"
              class="reset-base h-9 px-3 text-sm border rounded-lg border-n-weak bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12 focus:outline-none"
            />
            <span class="text-xs text-n-slate-11">
              {{ t('CONVERSATION.QUICK_SEND.NUMBER_HINT') }}
            </span>
          </label>

          <label class="flex flex-col gap-1">
            <span class="text-sm text-n-slate-12">
              {{ t('CONVERSATION.QUICK_SEND.MESSAGE_LABEL') }}
            </span>
            <textarea
              v-model="message"
              rows="4"
              :placeholder="t('CONVERSATION.QUICK_SEND.MESSAGE_PLACEHOLDER')"
              class="reset-base p-3 text-sm border rounded-lg resize-none border-n-weak bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12 focus:outline-none"
            />
          </label>

          <div class="flex justify-end gap-2">
            <Button
              :label="t('CONVERSATION.QUICK_SEND.CANCEL')"
              slate
              faded
              sm
              @click="close"
            />
            <Button
              :label="t('CONVERSATION.QUICK_SEND.SEND')"
              :is-loading="isSending"
              :disabled="!canSend"
              sm
              @click="send"
            />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
